// hooks/useUserInfo.ts
"use client";

import useSWR from "swr";
import apiInstance from "@/lib/axios";
import { getCompanyTimezone } from "@/lib/timezoneUtils";

interface Company {
  _id: string;
  value: string;
  timeZone: string;
  status?: string | null;
  subscriptionPlanId?: string | null;
  serviceFees: number
}

interface UserInfo {
  userName: string | null;
  userId: string | null;
  email: string | null;
  companies: Company[];
  companyIds: string[];
  role: string;
  editAccess: boolean;
  createdAt?: number; // Unix timestamp (seconds) for Intercom
  isInOnboarding?: boolean;
}

const fetcher = (url: string) => apiInstance.get(url).then(res => res.data);

export interface UseUserInfoOptions {
  enabled?: boolean;
}

function useUserInfo(options?: UseUserInfoOptions) {
  const enabled = options?.enabled !== false;
  const { data, error, isLoading } = useSWR<UserInfo>(
    enabled ? "/api/user/me" : null,
    fetcher,
    {
    revalidateOnFocus: false, // don’t refetch when tab focuses
    dedupingInterval: 5 * 60 * 1000, // cache for 5 min
    },
  );

  // Function to get timezone for a specific company
  const getCompanyTimezoneById = (companyId: string): string => {
    // First try to get from cookies
    const cookieTimezone = getCompanyTimezone(companyId);
    if (cookieTimezone) {
      return cookieTimezone;
    }

    // Fallback to company data from user info
    const company = data?.companies?.find(c => c._id === companyId);
    if (company?.timeZone) {
      return company.timeZone;
    }

    // Default fallback
    return "America/New_York";
  };

  return {
    userName: data?.userName ?? null,
    userId: data?.userId ?? null,
    email: data?.email ?? null,
    companies: data?.companies ?? [],
    companyIds: data?.companyIds ?? [],
    role: data?.role ?? '',
    editAccess: data?.editAccess ?? true,
    createdAt: data?.createdAt,
    isInOnboarding: data?.isInOnboarding ?? false,
    loading: isLoading,
    error: error?.response?.data?.message || error?.response?.data?.error || error?.message || null,
    getCompanyTimezoneById,
  };
}

export default useUserInfo;
